import { webRequest, getExtensionId } from "src/utils/extension";

export interface FetchOptions {
  retries?: number;
  wait?: number;
}

export function defaultIsOk(res: any): boolean {
  return res.ok;
}

export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface FetchWithRetryOptions {
  retries?: number;
  wait?: number;
  isOk?: typeof defaultIsOk;
}

class ServiceClient {
  extensionId: string;

  constructor() {
    this.extensionId = getExtensionId();
  }

  /*
   * { status, statusText, headers, data }
   */
  async fetch(
    url: string,
    opts: RequestInit = {},
    { retries = 5, wait: waitMs = 500 }: FetchOptions = {}
  ) {
    const res = await webRequest(this.extensionId, {
      url,
      init: {
        method: "GET",
        credentials: "include",
        ...opts,
      },
    });

    if (!res) {
      if (retries <= 0) throw new Error(`Undefined res: '${url}'`);
      // Something failed in the extension, just re-try it
      await wait(waitMs);
      return await this.fetch(url, opts, {
        retries: retries - 1,
        wait: waitMs * 2,
      });
    }

    return {
      request: res.payload,
      ...res.result,
      ok: 200 <= res.result.status && res.result.status < 300,
    };
  }

  /**
   * Same as fetch but retries on non-2xx response status.
   * Waits between retries (exponential backoff).
   */
  async fetchWithRetry(
    url: string,
    opts: RequestInit = {},
    {
      retries = 3,
      wait: waitMs = 1000,
      isOk = defaultIsOk,
    }: FetchWithRetryOptions = {}
  ) {
    let lastRes;
    for (let attempt = 0; attempt <= retries; attempt++) {
      lastRes = await this.fetch(url, opts);
      if (isOk(lastRes)) return lastRes;
      if (attempt < retries) {
        const delay = waitMs * Math.pow(2, attempt);
        await wait(delay);
      }
    }
    return lastRes!;
  }
}

export default ServiceClient;
